import { API_BASE, getAuthHeaders, apiFetch } from './api-fetch';

// ========================================
// USER MANAGEMENT API
// ========================================

export const adminUserAPI = {
  // Get all users (Cognito + KV merged)
  async getUsers() {
    try {
      const response = await apiFetch(`${API_BASE}/auth/users`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      return data.data?.users ?? [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get single user
  async getUser(id: string) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  // Create user via admin-createUser Lambda (Lambda #17)
  async createUser(userData: any) {
    try {
      console.log('🌐 admin-api.createUser - Starting request');
      console.log('📦 Original payload:', userData);

      // Map payload to admin-createUser Lambda format
      const createPayload: any = {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        role: userData.role,
      };

      // Add optional phone number if provided
      if (userData.phone && userData.phone.trim()) {
        createPayload.phoneNumber = userData.phone;
      }

      console.log('📦 Mapped payload for /admin/users:', createPayload);

      const response = await apiFetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(createPayload),
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');

        try {
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            console.error('❌ Server error response:', errorData);
          } else {
            const textError = await response.text();
            console.error('❌ Server error (non-JSON):', textError);
            errorData = { error: textError || `Server error: ${response.status}` };
          }
        } catch (parseError) {
          console.error('❌ Could not parse error response:', parseError);
          errorData = { error: `Server error: ${response.status} ${response.statusText}` };
        }

        throw new Error(errorData.error || errorData.message || 'Failed to create user');
      }

      const data = await response.json();
      console.log('✅ User created successfully:', data);

      // Return data in expected format (admin-createUser returns {success, data: {userId, email, message, generatedPassword?, passwordStatus}})
      return {
        id: data.data.userId,
        email: data.data.email,
        name: createPayload.name,
        role: createPayload.role,
        _meta: {
          message: data.data.message,
          generatedPassword: data.data.generatedPassword,
          passwordStatus: data.data.passwordStatus
        }
      };
    } catch (error: any) {
      console.error('❌ admin-api.createUser - Error:', error);

      // Re-throw with more context if it's a network error
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        throw new Error('Network error: Could not connect to server. Please check your internet connection.');
      }

      throw error;
    }
  },

  // Update user
  async updateUser(id: string, userData: any) {
    try {
      const encodedId = encodeURIComponent(id);
      console.log('🌐 admin-api.updateUser - Updating user:', id, '→ encoded:', encodedId);
      const response = await apiFetch(`${API_BASE}/admin/users/${encodedId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });
      
      console.log('📡 updateUser response status:', response.status, response.statusText);
      
      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');
        try {
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            console.error('❌ Update user error response:', errorData);
          } else {
            const textError = await response.text();
            console.error('❌ Update user error (non-JSON):', textError);
            errorData = { error: textError || `Server error: ${response.status}` };
          }
        } catch (parseError) {
          console.error('❌ Could not parse error response:', parseError);
          errorData = { error: `Server error: ${response.status} ${response.statusText}` };
        }
        throw new Error(errorData.error || errorData.message || 'Failed to update user');
      }
      const data = await response.json();
      console.log('✅ User updated successfully:', data);
      return data.user;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Toggle user active/inactive status via admin-toggleUserStatus Lambda (Lambda #20)
  async toggleUserStatus(id: string, enabled: boolean) {
    try {
      // Convert boolean to status string as required by Lambda #20
      const status = enabled ? 'enabled' : 'disabled';

      const response = await apiFetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle user status');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }
  },

  // Delete user via admin-deleteUser Lambda (Lambda #19)
  async deleteUser(id: string) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to delete user');
      }
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  async setUserPassword(userId: string, password: string, permanent: boolean = true) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/users/${encodeURIComponent(userId)}/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ password, permanent }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to set password');
      }
      const data = await response.json();
      return data.data ?? data;
    } catch (error) {
      console.error('Error setting user password:', error);
      throw error;
    }
  },

  // REMOVED: loginAsUser - Endpoint does not exist

  // Cleanup old Supabase users (migration helper)
  async cleanupSupabaseUsers() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/cleanup-supabase-users`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to cleanup users');
      }
      return await response.json();
    } catch (error) {
      console.error('Error cleaning up users:', error);
      throw error;
    }
  },

  // Resolve unknown agents by fetching from external API and storing locally
  async resolveAgents(agentIds: string[]): Promise<{
    resolved: number;
    alreadyExist: number;
    failed: number;
    agents: Array<{ agentId: string; name: string; email: string }>;
  }> {
    try {
      const response = await apiFetch(`${API_BASE}/admin/resolve-agents`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ agentIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to resolve agents');
      }
      return response.json();
    } catch (error) {
      console.error('Error resolving agents:', error);
      throw error;
    }
  },

  // Seed users
  async seedUsers(users: any[]) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/seed-users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ users }),
      });
      if (!response.ok) {
        throw new Error('Failed to seed users');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error seeding users:', error);
      throw error;
    }
  },
};

// ========================================
// SETTINGS API
// ========================================

export const adminSettingsAPI = {
  // Get all settings
  async getSettings() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/settings`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      const data = await response.json();
      return data.data?.settings ?? data.settings ?? [];
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  // Get single setting
  async getSetting(key: string) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/settings/${key}`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch setting');
      }
      const data = await response.json();
      return data.setting;
    } catch (error) {
      console.error('Error fetching setting:', error);
      throw error;
    }
  },

  // Update setting
  async updateSetting(key: string, settingData: any) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/settings/${key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ value: settingData }),
      });
      if (!response.ok) {
        throw new Error('Failed to update setting');
      }
      const data = await response.json();
      return data.data?.setting ?? data.setting;
    } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
    }
  },

  // Seed settings
  async seedSettings(settings: any[]) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/seed-settings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ settings }),
      });
      if (!response.ok) {
        throw new Error('Failed to seed settings');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error seeding settings:', error);
      throw error;
    }
  },
};

// ========================================
// ALERTS API
// ========================================

export const adminAlertsAPI = {
  // Get all alerts
  async getAlerts() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/alerts`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch alerts');
      }
      const data = await response.json();
      return data.data?.alerts ?? data.alerts ?? [];
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  },

  // Create alert
  async createAlert(alertData: any) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/alerts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(alertData),
      });
      if (!response.ok) {
        throw new Error('Failed to create alert');
      }
      const data = await response.json();
      return data.alert;
    } catch (error) {
      console.error('Error creating alert:', error);
      throw error;
    }
  },

  // Update alert
  async updateAlert(id: string, alertData: any) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/alerts/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(alertData),
      });
      if (!response.ok) {
        throw new Error('Failed to update alert');
      }
      const data = await response.json();
      return data.alert;
    } catch (error) {
      console.error('Error updating alert:', error);
      throw error;
    }
  },

  // Delete alert
  async deleteAlert(id: string) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/alerts/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to delete alert');
      }
      return true;
    } catch (error) {
      console.error('Error deleting alert:', error);
      throw error;
    }
  },
};

// ========================================
// AUDIT LOGS API
// ========================================

export const adminAuditAPI = {
  // Get all audit logs
  async getAuditLogs() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/audit-logs`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      const data = await response.json();
      return data.data?.logs ?? data.logs ?? [];
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  // Create audit log
  async createAuditLog(logData: any) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/audit-logs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(logData),
      });
      if (!response.ok) {
        throw new Error('Failed to create audit log');
      }
      const data = await response.json();
      return data.log;
    } catch (error) {
      console.error('Error creating audit log:', error);
      throw error;
    }
  },

  // Seed audit logs
  async seedAuditLogs(logs: any[]) {
    try {
      const response = await apiFetch(`${API_BASE}/admin/seed-audit-logs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ logs }),
      });
      if (!response.ok) {
        throw new Error('Failed to seed audit logs');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error seeding audit logs:', error);
      throw error;
    }
  },
};

// ========================================
// METRICS API
// ========================================

export const adminMetricsAPI = {
  // Get system metrics — endpoint: GET /admin/analytics/metrics
  async getMetrics() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/metrics`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }
      const data = await response.json();
      // Returns: { users: {total,active,byRole,growth}, properties: {total,active,sold,underOffer,byType},
      //           engagement: {period,inquiries,appointments,logins,totalAuditEvents}, system: {...} }
      return data.data;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      throw error;
    }
  },
};

// ========================================
// ANALYTICS API
// ========================================

export const adminAnalyticsAPI = {
  // Get user growth data
  async getUserGrowth() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/user-growth`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch user growth data');
      }
      const data = await response.json();
      return data.data?.breakdown ?? [];
    } catch (error) {
      console.error('Error fetching user growth analytics:', error);
      throw error;
    }
  },

  // Get revenue data — returns chart-ready breakdown array: [{ date, revenue, sources }]
  async getRevenue() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/revenue`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch revenue data');
      }
      const data = await response.json();
      // Lambda returns: { summary: {totalRevenue,currency,...}, breakdown: [{date,revenue,sources}], bySource }
      return data.data?.breakdown ?? [];
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
      throw error;
    }
  },

  // Get property listings — returns chart-ready array: [{ region (type), listings (active), sold }]
  async getPropertyListings() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/property-listings`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch property listings data');
      }
      const data = await response.json();
      // Lambda returns: { summary: {total,active,sold,underOffer,byType}, listings: [...] }
      // Aggregate listings by propertyType for the chart
      const listings: any[] = data.data?.listings ?? [];
      const byType: Record<string, { listings: number; sold: number }> = {};
      for (const l of listings) {
        const type = (l.propertyType || 'unknown').toLowerCase();
        if (!byType[type]) byType[type] = { listings: 0, sold: 0 };
        if ((l.status || '').toLowerCase() === 'sold') {
          byType[type].sold++;
        } else {
          byType[type].listings++;
        }
      }
      return Object.entries(byType)
        .filter(([, v]) => v.listings + v.sold > 0)
        .map(([region, v]) => ({ region, listings: v.listings, sold: v.sold }));
    } catch (error) {
      console.error('Error fetching property listings analytics:', error);
      throw error;
    }
  },

  // Get property types — returns chart-ready pie array: [{ name, value, color }]
  async getPropertyTypes() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/property-types`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch property types data');
      }
      const data = await response.json();
      // Lambda returns: { summary: {...}, types: [{type,count,percentage,byStatus,avgPrice,priceRange}] }
      const COLORS = ['#FDB022', '#666666', '#3B82F6', '#10B981', '#8B5CF6'];
      const types: any[] = data.data?.types ?? [];
      return types
        .filter((t: any) => t.count > 0)
        .map((t: any, i: number) => ({ name: t.type, value: t.count, color: COLORS[i % COLORS.length] }));
    } catch (error) {
      console.error('Error fetching property types analytics:', error);
      throw error;
    }
  },

  // Get lead sources — derived from engagement endpoint (no dedicated lead-sources Lambda exists)
  // Returns chart-ready array: [{ source, leads }]
  async getLeadSources() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/engagement`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch engagement data');
      }
      const data = await response.json();
      // Lambda returns: { byMetric: { inquiries, appointments, messages, activeUsers }, ... }
      const byMetric = data.data?.byMetric ?? {};
      return [
        { source: 'Inquiries', leads: byMetric.inquiries ?? 0 },
        { source: 'Appointments', leads: byMetric.appointments ?? 0 },
        { source: 'Messages', leads: byMetric.messages ?? 0 },
        { source: 'Active Users', leads: byMetric.activeUsers ?? 0 },
      ].filter((item) => item.leads > 0);
    } catch (error) {
      console.error('Error fetching lead sources analytics:', error);
      throw error;
    }
  },

  // Get agent performance — returns chart-ready array: [{ name, sales, commission }]
  async getAgentPerformance() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/agent-performance`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch agent performance data');
      }
      const data = await response.json();
      // Lambda returns: { summary: {...}, agents: [{name,deals:{closed},revenue:{commission,...},...}] }
      const agents: any[] = data.data?.agents ?? [];
      return agents.slice(0, 10).map((a: any) => ({
        name: a.name,
        sales: a.deals?.closed ?? 0,
        commission: Math.round((a.revenue?.commission ?? 0) / 1000), // in thousands (ZAR)
      }));
    } catch (error) {
      console.error('Error fetching agent performance analytics:', error);
      throw error;
    }
  },

  // Get engagement/activity data — returns chart-ready array: [{ time, views, leads }]
  async getActivity() {
    try {
      const response = await apiFetch(`${API_BASE}/admin/analytics/engagement`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to fetch engagement data');
      }
      const data = await response.json();
      // Lambda returns: { byMetric: { inquiries, appointments, messages, activeUsers }, ... }
      const byMetric = data.data?.byMetric ?? {};
      return [
        { time: 'Inquiries', views: byMetric.inquiries ?? 0, leads: byMetric.inquiries ?? 0 },
        { time: 'Appointments', views: byMetric.appointments ?? 0, leads: 0 },
        { time: 'Messages', views: byMetric.messages ?? 0, leads: 0 },
        { time: 'Active Users', views: byMetric.activeUsers ?? 0, leads: 0 },
      ];
    } catch (error) {
      console.error('Error fetching engagement analytics:', error);
      throw error;
    }
  },
};